import {useEffect, useRef, useState, useCallback} from "react";
import Cookies from "js-cookie";
import {
    ACCESS_TOKEN_KEY,
    COOKIES_COMPANY_ID,
    COOKIES_USER_ID,
    COOKIES_USER_ROLE,
} from "../helper/constants";

type Status = "connecting" | "connected" | "disconnected" | "error";
type MessageHandler = (data: any) => void;
type ConnectionHandler = (status: Status) => void;

interface WebSocketMessage {action: string; payload: any; headers: {
    Authorization: string
    userID: string
    companyID: string
}}
interface UserInfo {
    userID: string
    companyID: string
    token: string
    role: string
}
interface UseWebSocketOptions {
    onConnect?: () => void
    onMessage?: MessageHandler
    onConnectionChange?: ConnectionHandler
    onError?: (error: any) => void
    autoConnect?: boolean
}
const wsUrl = process.env.REACT_APP_WEBSOCKET_ENPOINT!;
const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_DELAY = 500;

function getUserInfo(): UserInfo {
    return {
        userID: Cookies.get(COOKIES_USER_ID) ?? "",
        companyID: Cookies.get(COOKIES_COMPANY_ID) ?? "",
        token: Cookies.get(ACCESS_TOKEN_KEY) ?? "",
        role: Cookies.get(COOKIES_USER_ROLE) ?? ""
    };
}

function buildMessage(action: string, payload: any): WebSocketMessage {
    const userInfo = getUserInfo();
    return {action, payload, headers: {
        Authorization: `Bearer ${userInfo.token}`,
        userID: userInfo.userID,
        companyID: userInfo.companyID,
    }};
}

export default function useWebSocket(p: UseWebSocketOptions = {}) {
    const [status, setStatus] = useState<Status>("disconnected");
    const socketRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
    const reconnectionsRef = useRef(0);
    const isIntentionalCloseRef = useRef(false);
    const onMessageRef = useRef(p.onMessage);
    const onConnectionChangeRef = useRef(p.onConnectionChange);
    const onErrorRef = useRef(p.onError);
    const onConnectRef = useRef(p.onConnect);
    const buildWebSocketUrl = useCallback(function (info: UserInfo): string {
        const params = new URLSearchParams({
            companyID: info.companyID,
            userID: info.userID,
            Authorization: `Bearer ${info.token}`
        });
        return `${wsUrl}?${params.toString()}`;
    }, [wsUrl]);
    const updateConnectionStatus = useCallback(function (status: Status) {
        setStatus(status);
        onConnectionChangeRef.current?.(status);
    }, []);
    const connect = useCallback(function () {
        let ws: WebSocket;
        if (socketRef.current?.readyState === WebSocket.CONNECTING ||
            socketRef.current?.readyState === WebSocket.OPEN) {
            return;
        }
        try {
            updateConnectionStatus("connecting");
            ws = new WebSocket(buildWebSocketUrl(getUserInfo()));
            ws.onopen = function () {
                updateConnectionStatus("connected");
                console.info("connected to the websocket");
                reconnectionsRef.current = 0;
                if (onConnectRef.current) {
                    onConnectRef.current();
                }
            };
            ws.onmessage = function (event) {
                try {
                    const data = JSON.parse(event.data);
                    if (onMessageRef.current) {
                        onMessageRef.current(data);
                    } else {
                        console.warn("You did not defined the message listener");
                    }
                } catch (error) {
                    console.error("Failed to parse message:", error);
                }
            };
            ws.onclose = function () {
                updateConnectionStatus("disconnected");
                socketRef.current = null;
                if (!isIntentionalCloseRef.current) {
                    handleReconnect();
                }
            };
            ws.onerror = function (error) {
                updateConnectionStatus("error");
                onErrorRef.current?.(error);
            };
            socketRef.current = ws;
        } catch (error) {
            updateConnectionStatus("error");
            handleReconnect();
        }
    }, [updateConnectionStatus]);
    const handleReconnect = useCallback(function () {
        const count = reconnectionsRef.current;
        const delay = RECONNECT_DELAY * Math.pow(1.5, count);
        if (reconnectionsRef.current >= MAX_RECONNECT_ATTEMPTS) {
            console.error("❌ Max reconnection attempts reached");
            setStatus("error");
            return;
        }
        console.warn(`⚠️ Reconnecting in ${delay}ms... (attempt ${count + 1})`);
        reconnectionsRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => {
            connect();
        }, delay);
    }, [connect]);
    const sendAction = useCallback((action: string, payload: any) => {
        const ws = socketRef.current;
        if (!ws) {
            console.error("WebSocket is not initialized");
            return false;
        }
        if (ws.readyState !== WebSocket.OPEN) {
            console.error("WebSocket is not open. Current state:", ws.readyState);
            return false;
        }
        try {
            ws.send(JSON.stringify(buildMessage(action, payload)));
            console.log("📤 Message sent:", action);
            return true;
        } catch (error) {
            console.error("Failed to send message:", error);
            return false;
        }
    }, [getUserInfo]);
    const disconnect = useCallback(function () {
        console.log("🔌 Manually closing WebSocket");
        isIntentionalCloseRef.current = true;
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }
        if (socketRef.current) {
            socketRef.current.close(1000, "Client disconnect");
            socketRef.current = null;
        }
        setStatus("disconnected");
    }, []);
    useEffect(function () {
        if (p.autoConnect) {
            isIntentionalCloseRef.current = false;
            connect();
        }
        return () => {
            isIntentionalCloseRef.current = true;
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (socketRef.current) {
                socketRef.current.close(1000, "Component unmount");
            }
        };
    }, [connect, p.autoConnect]);
    useEffect(function () {
        onMessageRef.current = p.onMessage;
        onConnectionChangeRef.current = p.onConnectionChange;
        onErrorRef.current = p.onError;
    }, [p.onMessage, p.onConnectionChange, p.onError]);
    return {status, sendAction, disconnect, reconnect: connect};
}
